import { useTripItineraryImports } from '@hooks'
import { countLabel } from '@/utils/plural'
import { queryKeys } from '@lib/queryKeys'
import {
	GetItineraryImportListItem,
} from '@orbit/api-types'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { darkOrange, orange, blue, success, red, black, white } from '@/theme/colors'
import { radii, media, motion } from '@/theme/tokens'
import { fontSizes, text, leading } from '@/theme/typography'
import { Button } from '@components/Button'
import Spinner from '@components/Spinner'
import { pickBannerImport, readDismissedImport, rememberDismissedImport } from './importBanner'

type BannerVariant = 'progress' | 'success' | 'warn'

interface Props {
	tripId: string
}

const ImportStatusBanner: React.FC<Props> = ({ tripId }) => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	// Seeded from storage so a dismissal survives a refresh, and re-read when the
	// route moves to another trip (the page keeps this component mounted).
	const [dismissed, setDismissed] = useState(() => ({ tripId, importId: readDismissedImport(tripId) }))
	if (dismissed.tripId !== tripId) setDismissed({ tripId, importId: readDismissedImport(tripId) })
	const lastSeenStatusByIdRef = useRef<Map<string, GetItineraryImportListItem['status']>>(new Map())
	const { data } = useTripItineraryImports(tripId)

	useEffect(() => {
		const items = data?.items ?? []
		const lastSeen = lastSeenStatusByIdRef.current
		let shouldInvalidate = false
		for (const item of items) {
			const prev = lastSeen.get(item.id)
			lastSeen.set(item.id, item.status)
			if (
				prev &&
				(prev === 'Pending' || prev === 'Processing') &&
				item.status === 'Success'
			) {
				shouldInvalidate = true
			}
		}
		if (shouldInvalidate) {
			for (const key of queryKeys.itineraryContentKeys(tripId)) {
				queryClient.invalidateQueries({ queryKey: key })
			}
		}
	}, [data, queryClient, tripId])

	const relevant = pickBannerImport(data?.items ?? [], dismissed.importId)
	if (!relevant) return null

	const variant = variantForStatus(relevant.status)
	const onOpen = () => {
		navigate(`/trips/${tripId}/import?job=${relevant.id}`)
	}

	return (
		<BannerRoot
			$variant={variant}
			role="button"
			tabIndex={0}
			onClick={onOpen}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					onOpen()
				}
			}}
		>
			<IconBox $variant={variant}>
				{variant === 'progress' ? <Spinner size={14} color={white} trackColor={`${white}59`} /> : variant === 'success' ? <Glyph>✓</Glyph> : <Glyph>!</Glyph>}
			</IconBox>
			<Message>{messageForItem(relevant)}</Message>
			<CTA $variant={variant}>{ctaForVariant(variant)} →</CTA>
			<DismissButton
				type="button"
				aria-label="Dismiss"
				onClick={e => {
					e.stopPropagation()
					rememberDismissedImport(tripId, relevant.id)
					setDismissed({ tripId, importId: relevant.id })
				}}
			>
				×
			</DismissButton>
		</BannerRoot>
	)
}

export default ImportStatusBanner

const variantForStatus = (status: GetItineraryImportListItem['status']): BannerVariant => {
	if (status === 'Pending' || status === 'Processing') return 'progress'
	if (status === 'Success') return 'success'
	return 'warn'
}

const messageForItem = (item: GetItineraryImportListItem): string => {
	switch (item.status) {
		case 'Pending':
		case 'Processing':
			return "Adding to your itinerary — we'll let you know when it's done"
		case 'Success':
			return `${countLabel(item.createdEventCount, 'event')} added to your itinerary`
		case 'NoEventsFound':
			return "Last import didn't find any events"
		case 'Failed':
			return 'Last import failed — open to retry'
	}
}

const ctaForVariant = (variant: BannerVariant): string => {
	if (variant === 'progress') return 'View progress'
	if (variant === 'success') return 'Review'
	return 'Open'
}

const variantBg: Record<BannerVariant, string> = {
	progress: `${blue}14`,
	success: `${success}1f`,
	warn: `${red}1a`,
}

const variantBorder: Record<BannerVariant, string> = {
	progress: `${blue}38`,
	success: `${success}52`,
	warn: `${red}4d`,
}

const variantIconBg: Record<BannerVariant, string> = {
	progress: blue,
	success: success,
	warn: red,
}

const variantCTA: Record<BannerVariant, string> = {
	progress: blue,
	success: black,
	warn: darkOrange,
}

const BannerRoot = styled.div<{ $variant: BannerVariant }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 14px;
	margin: 0 0 16px;
	border-radius: ${radii.inner};
	background: ${({ $variant }) => variantBg[$variant]};
	border: 1px solid ${({ $variant }) => variantBorder[$variant]};
	cursor: pointer;
	transition: background ${motion.fast};

	&:hover {
		background: ${({ $variant }) =>
			$variant === 'progress'
				? `${blue}1f`
				: $variant === 'success'
					? `${success}29`
					: `${red}24`};
	}

	&:focus-visible {
		outline: 2px solid ${orange};
		outline-offset: 2px;
	}

	${media.phone} {
		padding: 8px 12px;
		gap: 10px;
	}
`

const IconBox = styled.div<{ $variant: BannerVariant }>`
	width: 28px;
	height: 28px;
	border-radius: ${radii.small};
	background: ${({ $variant }) => variantIconBg[$variant]};
	color: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`

const Glyph = styled.span`
	${text.label}
	font-weight: 900;
	line-height: ${leading.none};
`

const Message = styled.div`
	flex: 1;
	min-width: 0;
	${text.labelSm}
	color: ${black};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	${media.phone} {
		white-space: normal;
	}
`

const CTA = styled.div<{ $variant: BannerVariant }>`
	${text.labelSm}
	color: ${({ $variant }) => variantCTA[$variant]};
	flex-shrink: 0;

	${media.phone} {
		display: none;
	}
`

const DismissButton = styled(Button).attrs({ variant: 'icon' as const })`
	width: 28px;
	height: 28px;
	font-size: ${fontSizes.title};
	line-height: ${leading.none};
`
